import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldAlert, Check, X, Loader2, ChevronDown } from 'lucide-react';

/**
 * AIModerationReports — Admin panel section to review content flagged by the
 * AI content-moderation pipeline (GPT-4o-mini) on Gim Toli + Studio stories.
 */
const AIModerationReports = () => {
  const [status, setStatus] = useState('pending');
  const [severityFilter, setSeverityFilter] = useState('');
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ pending_count: 0, critical_count: 0 });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/moderation/reports', {
        params: { status, severity: severityFilter || undefined, limit: 100 },
      });
      setReports(res.data?.reports || []);
      setCounts({
        pending_count: res.data?.pending_count || 0,
        critical_count: res.data?.critical_count || 0,
      });
    } catch (e) {
      toast.error("Impossible de charger les rapports");
      setReports([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, severityFilter]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      await axios.post(`/api/admin/moderation/reports/${id}/${action}`, { note: '' });
      toast.success(action === 'resolve' ? 'Marqué traité' : 'Faux positif ignoré');
      setReports(prev => prev.filter(r => r.id !== id));
      setCounts(c => ({ ...c, pending_count: Math.max(0, c.pending_count - 1) }));
    } catch (e) {
      toast.error("Action impossible");
    } finally { setActing(null); }
  };

  const sevColor = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-blue-500 text-white',
    safe: 'bg-gray-500 text-white',
  };

  const sourceLabel = {
    gim_toli: 'Vocaux',
    podcast: 'Podcast',
    message: 'Message',
    comment: 'Commentaire',
    photo_post: 'Photo partagée',
    photo_comment: 'Commentaire photo',
    studio_story: 'Histoire audio',
  };

  const [search, setSearch] = useState('');
  const visibleReports = reports.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.text_excerpt || r.text || '').toLowerCase().includes(q)
      || (r.category || '').toLowerCase().includes(q)
      || (r.source_type || '').toLowerCase().includes(q)
      || (r.owner_name || '').toLowerCase().includes(q);
  });

  const resolveAll = async () => {
    if (!window.confirm(`Marquer les ${visibleReports.length} signalements affichés comme traités ?`)) return;
    for (const r of visibleReports) {
      try { await axios.post(`/api/admin/moderation/reports/${r.id}/resolve`, { note: 'bulk' }); } catch (e) { /* continue */ }
    }
    toast.success('Signalements traités');
    load();
  };

  return (
    <div className="space-y-4" data-testid="ai-moderation-reports">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-400" />
            Modération IA — contenu signalé
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Détection automatique par GPT-4o-mini : menaces de mort, propos racistes, incitations à la violence, insultes graves.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 bg-red-500/20 border border-red-500/40 rounded-lg text-sm font-semibold">
            {counts.critical_count} critique{counts.critical_count > 1 ? 's' : ''}
          </span>
          <span className="px-3 py-1.5 bg-orange-500/20 border border-orange-500/40 rounded-lg text-sm font-semibold">
            {counts.pending_count} en attente
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['pending', 'resolved', 'dismissed', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-sm ${status === s ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
            data-testid={`mod-filter-${s}`}
          >
            {s === 'pending' ? 'En attente' : s === 'resolved' ? 'Traités' : s === 'dismissed' ? 'Ignorés' : 'Tous'}
          </button>
        ))}
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="px-3 py-1.5 rounded-full bg-white/5 text-sm border border-white/10"
          data-testid="mod-filter-severity"
        >
          <option value="">Toutes sévérités</option>
          <option value="critical">Critique</option>
          <option value="high">Élevée</option>
          <option value="medium">Moyenne</option>
          <option value="low">Faible</option>
        </select>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher (texte, catégorie, auteur)…"
          className="px-3 py-1.5 rounded-full bg-white/5 text-sm border border-white/10 min-w-[220px] placeholder-gray-500 focus:outline-none focus:border-purple-500/60"
          data-testid="mod-search-input"
        />
        <button onClick={load} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-sm border border-white/10" data-testid="mod-refresh-btn">
          Actualiser
        </button>
        {status === 'pending' && visibleReports.length > 0 && (
          <button onClick={resolveAll} className="px-3 py-1.5 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-sm text-emerald-200 font-semibold" data-testid="mod-resolve-all-btn">
            Tout marquer traité ({visibleReports.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
        </div>
      ) : visibleReports.length === 0 ? (
        <div className="text-center py-12 text-gray-400 rounded-2xl bg-white/5 border border-white/10">
          <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="font-semibold">Aucun signalement en attente</p>
          <p className="text-xs mt-1">L'IA scanne en continu les contenus publiés.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleReports.map(r => (
            <div key={r.id} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`mod-report-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${sevColor[r.severity] || sevColor.safe}`}>{r.severity}</span>
                  <span className="text-xs text-gray-400">{sourceLabel[r.source_type] || r.source_type}</span>
                  {r.categories?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {r.categories.map((c, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-200">{c}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-500">{new Date(r.created_at).toLocaleString('fr-FR')}</span>
              </div>

              {r.owner && (
                <div className="flex items-center gap-2 mb-2">
                  {r.owner.avatar_url && <img src={r.owner.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                  <span className="text-sm text-gray-300">par {r.owner.name} {r.owner.username && <span className="text-gray-500">@{r.owner.username}</span>}</span>
                </div>
              )}

              <div className="bg-black/40 rounded-lg p-3 text-sm whitespace-pre-wrap break-words" data-testid={`mod-report-${r.id}-excerpt`}>
                {r.text_excerpt}
              </div>

              {r.reason && (
                <p className="text-xs text-yellow-300/80 mt-2 italic">
                  <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                  {r.reason}
                </p>
              )}

              {status === 'pending' && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => act(r.id, 'resolve')}
                    disabled={acting === r.id + 'resolve'}
                    className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-semibold flex items-center justify-center gap-1"
                    data-testid={`mod-report-${r.id}-resolve`}
                  >
                    {acting === r.id + 'resolve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Traité
                  </button>
                  <button
                    onClick={() => act(r.id, 'dismiss')}
                    disabled={acting === r.id + 'dismiss'}
                    className="flex-1 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 text-sm font-semibold flex items-center justify-center gap-1"
                    data-testid={`mod-report-${r.id}-dismiss`}
                  >
                    {acting === r.id + 'dismiss' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    Ignorer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIModerationReports;
